import React, { useState, useEffect } from 'react';
import { getOccupancyReport, getHostels, getRoomsByHostel, getAllocations, getHostelComplaints } from '../../../services/hostelService';
import toast from 'react-hot-toast';

const HostelReports = ({ coachingId }) => {
  const [occupancy, setOccupancy] = useState({ total: 0, occupied: 0, vacant: 0 });
  const [hostels, setHostels] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadReports(); }, [coachingId]);

  const loadReports = async () => {
    setLoading(true);
    const [occ, h, alloc, comp] = await Promise.all([
      getOccupancyReport(coachingId),
      getHostels(coachingId),
      getAllocations(coachingId),
      getHostelComplaints(coachingId),
    ]);
    setOccupancy(occ);
    setHostels(h);
    setAllocations(alloc);
    setComplaints(comp);
    setLoading(false);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  const occPercent = occupancy.total > 0 ? Math.round((occupancy.occupied / occupancy.total) * 100) : 0;
  const complaintStats = {
    open: complaints.filter(c => c.status === 'open').length,
    assigned: complaints.filter(c => c.status === 'assigned').length,
    inProgress: complaints.filter(c => c.status === 'in-progress').length,
    resolved: complaints.filter(c => c.status === 'resolved').length,
    closed: complaints.filter(c => c.status === 'closed').length,
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Reports & Analytics</h2>

      {/* Occupancy Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 border rounded-xl p-4">
          <p className="text-sm text-gray-500">Total Beds</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-white">{occupancy.total}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 border rounded-xl p-4">
          <p className="text-sm text-gray-500">Occupied</p>
          <p className="text-2xl font-bold text-red-600">{occupancy.occupied}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 border rounded-xl p-4">
          <p className="text-sm text-gray-500">Vacant</p>
          <p className="text-2xl font-bold text-green-600">{occupancy.vacant}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 border rounded-xl p-4">
          <p className="text-sm text-gray-500">Occupancy Rate</p>
          <p className="text-2xl font-bold text-blue-600">{occPercent}%</p>
        </div>
      </div>

      {/* Hostels Summary */}
      <div className="bg-white dark:bg-gray-800 border rounded-xl p-4">
        <h3 className="font-semibold text-gray-800 dark:text-white mb-3">Hostels ({hostels.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {hostels.map(h => (
            <div key={h.id} className="border rounded-lg p-3">
              <p className="font-medium text-gray-800 dark:text-white">{h.name}</p>
              <p className="text-xs text-gray-500">{h.type} • {h.status}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Active Allocations */}
      <div className="bg-white dark:bg-gray-800 border rounded-xl p-4">
        <h3 className="font-semibold text-gray-800 dark:text-white mb-3">Active Allocations ({allocations.length})</h3>
        {allocations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b">
                <th className="text-left py-2 text-gray-500">Student</th>
                <th className="text-left py-2 text-gray-500">Hostel</th>
                <th className="text-left py-2 text-gray-500">Room</th>
              </tr></thead>
              <tbody>
                {allocations.slice(0, 20).map(a => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="py-2 text-gray-800 dark:text-white">{a.studentName || 'Student'}</td>
                    <td className="py-2 text-gray-500">{a.hostelId?.substring(0,8)}</td>
                    <td className="py-2 text-gray-500">{a.roomId?.substring(0,8)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-gray-400 text-sm">No active allocations.</p>}
      </div>

      {/* Complaint Summary */}
      <div className="bg-white dark:bg-gray-800 border rounded-xl p-4">
        <h3 className="font-semibold text-gray-800 dark:text-white mb-3">Complaint Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="text-center p-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-lg font-bold text-red-600">{complaintStats.open}</p>
            <p className="text-xs text-gray-500">Open</p>
          </div>
          <div className="text-center p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <p className="text-lg font-bold text-yellow-600">{complaintStats.assigned}</p>
            <p className="text-xs text-gray-500">Assigned</p>
          </div>
          <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-lg font-bold text-blue-600">{complaintStats.inProgress}</p>
            <p className="text-xs text-gray-500">In Progress</p>
          </div>
          <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-lg font-bold text-green-600">{complaintStats.resolved}</p>
            <p className="text-xs text-gray-500">Resolved</p>
          </div>
          <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-lg font-bold text-gray-600 dark:text-gray-300">{complaintStats.closed}</p>
            <p className="text-xs text-gray-500">Closed</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostelReports;
